import type { Draft } from '@anchor-comms/shared';
import { apiBase } from '@/lib/api';

export interface GenerateCallbacks {
  onToken: (delta: string) => void;
  onComplete: (draft: Draft) => void;
  onError: (code: string, message: string) => void;
}

/**
 * POST + SSE. Uses fetch() with a streaming reader so we can attach the auth
 * cookie (EventSource is GET-only and can't include credentials cross-origin).
 *
 * Returns an `abort` function that the caller can invoke to cancel.
 */
export function streamGenerate(
  conversationId: string,
  repContext: string | null,
  cb: GenerateCallbacks,
): { abort: () => void } {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(`${apiBase}/api/v1/conversations/${conversationId}/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify(repContext ? { repContext } : {}),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        let code = 'http_error';
        let message = `Request failed (${res.status})`;
        try {
          const parsed = JSON.parse(text);
          code = parsed.error?.code ?? code;
          message = parsed.error?.message ?? message;
        } catch {
          // not JSON; keep generic
        }
        cb.onError(code, message);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE events end with a blank line
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          parseEvent(rawEvent, cb);
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      cb.onError('network', (err as Error).message || 'Network error');
    }
  })();

  return { abort: () => controller.abort() };
}

function parseEvent(raw: string, cb: GenerateCallbacks) {
  let event = 'message';
  let data = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  if (!data) return;
  try {
    const parsed = JSON.parse(data);
    if (event === 'token') cb.onToken(parsed.delta);
    else if (event === 'complete') cb.onComplete(parsed.draft);
    else if (event === 'error') cb.onError(parsed.code, parsed.message);
  } catch {
    // ignore malformed event
  }
}
